import React, { FC, useState, useRef, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material'
import { MedicationInsertButton } from '@/apps/latest/components/molecules/MedicationInsertButton'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { insertAtCursor } from '@/shared/utils/textFieldHelper'
import { sanitizeInput } from '@/shared/utils/sanitize'
import { Soaps, SoapSectionKeys } from '@/shared/types/yakurekiSoap'
import { SectionHeading } from '@/components/atoms/SectionHeading'
import { MetaText } from '@/components/atoms/MetaText'
import { DialogHeader } from '@/components/atoms/DialogHeader'
import { RADIUS } from '@/shared/styles/radii'
import {
  NOTION_DIALOG_PAPER_SX,
  NOTION_DIALOG_TEXTFIELD_SX,
} from '@/shared/styles/dialogStyles'

const FOCUS_DELAY_MS = 100

interface BulkReplaceDialogProps {
  open: boolean
  onClose: () => void
  soapContent: Soaps | undefined
  medications: string[]
  onReplace: (searchText: string, replaceText: string) => Promise<void>
}

export const BulkReplaceDialog: FC<BulkReplaceDialogProps> = ({
  open,
  onClose,
  soapContent,
  medications,
  onReplace,
}) => {
  const [searchText, setSearchText] = useState('')
  const [replaceText, setReplaceText] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [matchCount, setMatchCount] = useState(0)

  const searchRef = useRef<HTMLInputElement>(null)
  const replaceRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchRef.current?.focus()
      }, FOCUS_DELAY_MS)
    }
  }, [open])

  useEffect(() => {
    if (!searchText || !soapContent) {
      setMatchCount(0)
      return
    }
    let count = 0
    const searchLower = searchText.toLowerCase()
    Object.keys(soapContent).forEach((key) => {
      if (key === 'id') return
      const typedKey = key as SoapSectionKeys
      const items = soapContent[typedKey]
      if (Array.isArray(items)) {
        items.forEach((item) => {
          if (
            item &&
            item.content &&
            item.content.toLowerCase().includes(searchLower)
          ) {
            const matches =
              item.content.toLowerCase().split(searchLower).length - 1
            count += matches
          }
        })
      }
    })
    setMatchCount(count)
  }, [searchText, soapContent])

  const handleSearchInsert = (text: string) => {
    insertAtCursor(searchRef, searchText, setSearchText, text)
  }

  const handleReplaceInsert = (text: string) => {
    insertAtCursor(replaceRef, replaceText, setReplaceText, text)
  }

  const handleClose = () => {
    if (!isProcessing) {
      setSearchText('')
      setReplaceText('')
      onClose()
    }
  }

  const handleExecuteReplace = async () => {
    if (!searchText || replaceText === undefined) return
    const sanitizedSearchText = sanitizeInput(searchText)
    const sanitizedReplaceText = sanitizeInput(replaceText)
    setIsProcessing(true)
    try {
      await onReplace(sanitizedSearchText, sanitizedReplaceText)
      handleClose()
    } catch (_error) {
      // 呼び出し側で toast 表示
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isProcessing}
      sx={{ '& .MuiDialog-paper': NOTION_DIALOG_PAPER_SX }}
    >
      <DialogHeader
        title="一括置換"
        onClose={handleClose}
        disabled={isProcessing}
      />

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <SectionHeading sx={{ mb: 0.5 }}>検索する文字列</SectionHeading>
            <TextField
              ref={searchRef}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fullWidth
              size="small"
              disabled={isProcessing}
              placeholder="置換前のテキスト"
              sx={NOTION_DIALOG_TEXTFIELD_SX}
            />
            <Box sx={{ mt: 0.75 }}>
              <MedicationInsertButton
                medications={medications}
                onInsert={handleSearchInsert}
                disabled={isProcessing}
              />
            </Box>
            {searchText && (
              <MetaText
                sx={{
                  mt: 0.75,
                  color:
                    matchCount > 0
                      ? 'text.secondary'
                      : colorTokens.state.warnFg,
                }}
              >
                {matchCount > 0
                  ? `${matchCount} 件の置換対象`
                  : '置換対象が見つかりません'}
              </MetaText>
            )}
          </Box>

          <Box>
            <SectionHeading sx={{ mb: 0.5 }}>置換する文字列</SectionHeading>
            <TextField
              ref={replaceRef}
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              fullWidth
              size="small"
              disabled={isProcessing}
              placeholder="置換後のテキスト (空欄で削除)"
              sx={NOTION_DIALOG_TEXTFIELD_SX}
            />
            <Box sx={{ mt: 0.75 }}>
              <MedicationInsertButton
                medications={medications}
                onInsert={handleReplaceInsert}
                disabled={isProcessing}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: `1px solid ${colorTokens.border.subtle}`,
          gap: 0.5,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={isProcessing}
          sx={{
            textTransform: 'none',
            height: 32,
            px: 1.75,
            borderRadius: `${RADIUS.full}px`,
            fontSize: 13,
            fontWeight: 500,
            color: 'text.primary',
            minWidth: 0,
            '&:hover': { bgcolor: colorTokens.overlay.selected },
          }}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleExecuteReplace}
          disabled={!searchText || matchCount === 0 || isProcessing}
          variant="contained"
          disableElevation
          sx={{
            textTransform: 'none',
            height: 32,
            px: 1.75,
            borderRadius: `${RADIUS.full}px`,
            fontSize: 13,
            fontWeight: 500,
            bgcolor: colorTokens.accent.primary,
            minWidth: 0,
            '&:hover': { bgcolor: colorTokens.accent.primaryHover },
            '&.Mui-disabled': {
              bgcolor: colorTokens.overlay.selected,
              color: 'text.disabled',
            },
          }}
        >
          {isProcessing ? '処理中…' : '置換を実行'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
